import { useEffect, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import {
  ShieldCheck, Users, Package, Star, Receipt, LayoutDashboard, Search, Ban, RotateCcw,
  EyeOff, Eye, Trash2, Check, X, Building2, IdCard, Loader2, BadgeCheck, ShieldAlert, CreditCard, Percent, Tags, Plus, Flag, FileText, LifeBuoy, Send, ChevronLeft, Clock, Play, AlertCircle, BarChart3, TrendingUp, AlertTriangle, Mail, UserCog, Image as ImageIcon, HardHat, Download, Printer, Wallet as WalletIcon,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

const peso = (n: number) => "₱" + (Number(n) || 0).toLocaleString("en-PH");

const NAV: { group: string; items: { key: string; label: string; Icon: any }[] }[] = [
  { group: "Main", items: [{ key: "dashboard", label: "Dashboard", Icon: LayoutDashboard }] },
  { group: "User Management", items: [
    { key: "customers", label: "Customers", Icon: Users },
    { key: "verifications", label: "Verifications", Icon: ShieldCheck },
    { key: "admins", label: "Admin Users", Icon: UserCog },
  ] },
  { group: "Marketplace", items: [
    { key: "listings", label: "Listings", Icon: Package },
    { key: "categories", label: "Categories", Icon: Tags },
    { key: "reviews", label: "Reviews", Icon: Star },
  ] },
  { group: "Finance", items: [
    { key: "transactions", label: "Transactions", Icon: Receipt },
    { key: "fees", label: "Fees & Pricing", Icon: Percent },
    { key: "credit", label: "Credit", Icon: CreditCard },
  ] },
  { group: "Trust & Safety", items: [
    { key: "disputes", label: "Disputes", Icon: ShieldAlert },
    { key: "flags", label: "Flags", Icon: Flag },
    { key: "media", label: "Media Moderation", Icon: ImageIcon },
    { key: "support", label: "Support", Icon: LifeBuoy },
  ] },
  { group: "Insights", items: [
    { key: "reports", label: "Reports", Icon: FileText },
    { key: "analytics", label: "Analytics", Icon: BarChart3 },
  ] },
  { group: "Site & Content", items: [
    { key: "site", label: "Site Management", Icon: ImageIcon },
    { key: "legal", label: "Legal", Icon: FileText },
  ] },
  { group: "System", items: [
    { key: "jobs", label: "Background Jobs", Icon: Clock },
    { key: "outbox", label: "Email Outbox", Icon: Mail },
  ] },
];
const ALL_KEYS = NAV.flatMap((g) => g.items.map((i) => i.key));
const TITLES: Record<string, string> = Object.fromEntries(NAV.flatMap((g) => g.items.map((i) => [i.key, i.label])));
const REASON_LABELS: Record<string, string> = { not_received: "Not received", not_as_described: "Not as described", quality: "Quality issue", other: "Other" };

export default function Admin() {
  const { user, loading } = useAuth();
  const [, navigate] = useLocation();
  const search = useSearch();
  const isAdmin = user?.userType === "admin";
  const initial = new URLSearchParams(search).get("tab") || "dashboard";
  const [view, setView] = useState(ALL_KEYS.includes(initial) ? initial : "dashboard");

  useEffect(() => { if (!loading && !isAdmin) navigate("/"); }, [loading, isAdmin, navigate]);
  function pick(k: string) {
    setView(k);
    const sp = new URLSearchParams(window.location.search); sp.set("tab", k);
    window.history.replaceState(null, "", `${window.location.pathname}?${sp.toString()}`);
    window.scrollTo(0, 0);
  }
  if (loading || !isAdmin) return <div className="container-x py-20 text-center text-mut">Loading…</div>;

  return (
    <div className="flex min-h-[calc(100vh-4rem)]">
      {/* Sidebar */}
      <aside className="hidden md:flex w-60 shrink-0 flex-col bg-navy text-white/85 sticky top-16 self-start h-[calc(100vh-4rem)] overflow-y-auto">
        <div className="px-4 pt-4 pb-3">
          <div className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-brand-gold" /><span className="font-extrabold text-white">Admin Portal</span></div>
          <div className="mt-3 flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-2.5 py-2">
            <div className="w-7 h-7 rounded-full bg-navy-700 grid place-items-center text-white text-xs font-bold">{(user?.firstName?.[0] || "A")}{(user?.lastName?.[0] || "")}</div>
            <div className="min-w-0"><div className="text-[13px] font-bold text-white truncate">{user?.firstName} {user?.lastName}</div><span className="text-[10px] font-extrabold uppercase tracking-wide text-navy bg-brand-gold rounded px-1.5 py-[1px]">Super Admin</span></div>
          </div>
        </div>
        <nav className="px-2 pb-6 flex-1">
          {NAV.map((g) => (
            <div key={g.group}>
              <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold px-3 pt-4 pb-1">{g.group}</div>
              {g.items.map(({ key, label, Icon }) => (
                <button key={key} onClick={() => pick(key)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-semibold text-left transition ${view === key ? "bg-navy-700 text-white" : "text-white/75 hover:bg-white/10 hover:text-white"}`}>
                  <Icon className="w-[17px] h-[17px] shrink-0" />{label}
                </button>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        {/* Mobile section picker */}
        <div className="md:hidden sticky top-16 z-10 bg-white border-b border-line px-4 py-2 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-brand-green shrink-0" />
          <select value={view} onChange={(e) => pick(e.target.value)} className="field h-9 flex-1">
            {NAV.map((g) => <optgroup key={g.group} label={g.group}>{g.items.map((i) => <option key={i.key} value={i.key}>{i.label}</option>)}</optgroup>)}
          </select>
        </div>
        <div className="px-4 sm:px-6 py-6 max-w-[1200px]">
          <h1 className="text-xl font-extrabold text-navy mb-4 hidden md:block">{TITLES[view]}</h1>
          {view === "dashboard" && <DashboardSection go={pick} />}
          {view === "customers" && <CustomersSection />}
          {view === "verifications" && <VerificationsSection />}
          {view === "admins" && <AdminUsersSection selfId={user?.id} />}
          {view === "listings" && <ListingsTab />}
          {view === "categories" && <CategoriesTab />}
          {view === "reviews" && <ReviewsTab />}
          {view === "transactions" && <TransactionsTab />}
          {view === "fees" && <FeesTab />}
          {view === "credit" && <CreditTab />}
          {view === "disputes" && <DisputesTab />}
          {view === "flags" && <FlagsTab />}
          {view === "support" && <SupportTab />}
          {view === "reports" && <ReportsSection />}
          {view === "analytics" && <AnalyticsTab />}
          {view === "media" && <MediaModerationSection />}
          {view === "site" && <SiteSection />}
          {view === "legal" && <LegalTab />}
          {view === "jobs" && <JobsTab />}
          {view === "outbox" && <EmailOutboxSection />}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: any }) {
  return <div className="card p-4"><div className="text-2xl font-extrabold text-navy">{value}</div><div className="text-xs text-mut mt-1">{label}</div></div>;
}

function Overview() {
  const [s, setS] = useState<any>(null);
  useEffect(() => { api.adminStats().then(setS).catch(() => setS(null)); }, []);
  if (!s) return <Loading />;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Total users" value={s.users.total} />
        <Stat label="Verified" value={s.users.verified} />
        <Stat label="Suspended" value={s.users.suspended} />
        <Stat label="Pending verifications" value={s.pendingVerifications} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Orders" value={s.transactions.orders} />
        <Stat label="Completed orders" value={s.transactions.ordersCompleted} />
        <Stat label="Gross value (GMV)" value={peso(s.transactions.gmv)} />
        <Stat label="Open disputes" value={s.openDisputes ?? 0} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Pending verifications" value={s.pendingVerifications} />
        <Stat label="Credit outstanding" value={peso(s.creditOutstanding ?? 0)} />
      </div>
      <div>
        <h3 className="font-bold text-navy mb-2">Live listings</h3>
        <div className="grid grid-cols-3 sm:grid-cols-7 gap-3">
          <Stat label="Items" value={s.listings.items} />
          <Stat label="Services" value={s.listings.services} />
          <Stat label="Companies" value={s.listings.companies} />
          <Stat label="Résumés" value={s.listings.people} />
          <Stat label="Jobs" value={s.listings.jobs} />
          <Stat label="Projects" value={s.listings.projects} />
          <Stat label="RFQs" value={s.listings.rfqs} />
        </div>
      </div>
    </div>
  );
}

function UsersTab() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<any[] | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  async function load() { setRows(await api.adminUsers(q).catch(() => [])); }
  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [q]);
  async function toggle(u: any) {
    setBusy(u.id);
    try { u.accountStatus === "suspended" ? await api.adminReactivate(u.id) : await api.adminSuspend(u.id); await load(); } catch { /* ignore */ }
    finally { setBusy(null); }
  }
  return (
    <div>
      <SearchBox value={q} onChange={setQ} placeholder="Search by name or email…" />
      {!rows ? <Loading /> : (
        <div className="space-y-2 mt-3">
          {rows.length === 0 && <Empty text="No users match." />}
          {rows.map((u) => (
            <div key={u.id} className="card p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <Link href={`/u/${u.id}`} className="font-semibold text-navy hover:text-brand-red inline-flex items-center gap-1">{u.name || u.email}{u.verified && <BadgeCheck className="w-4 h-4 text-brand-green" />}</Link>
                  <div className="text-xs text-mut truncate">{u.email}{u.userType === "admin" ? " · admin" : ""}</div>
                  {u.identityReviewPending && <span className="chip bg-amber-50 text-amber-700 mt-1"><ShieldAlert className="w-3.5 h-3.5" />Identity change — review</span>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {u.accountStatus === "suspended"
                    ? <span className="chip bg-brand-red/10 text-brand-red">Suspended</span>
                    : <span className="chip bg-brand-green/10 text-brand-greendark">Active</span>}
                  <button onClick={() => setEditId(editId === u.id ? null : u.id)} className="btn-outline h-8 px-3 text-xs" data-testid={`edit-${u.id}`}>Edit</button>
                  {u.userType !== "admin" && (
                    <button onClick={() => toggle(u)} disabled={busy === u.id} className={`h-8 px-3 text-xs rounded-lg font-semibold disabled:opacity-50 ${u.accountStatus === "suspended" ? "btn-navy" : "btn-outline text-brand-red"}`}>
                      {u.accountStatus === "suspended" ? <><RotateCcw className="w-3.5 h-3.5" />Reactivate</> : <><Ban className="w-3.5 h-3.5" />Suspend</>}
                    </button>
                  )}
                </div>
              </div>
              {editId === u.id && <AdminUserEditor u={u} onDone={() => { setEditId(null); load(); }} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminUserEditor({ u, onDone }: { u: any; onDone: () => void }) {
  const [firstName, setFirstName] = useState(u.firstName || "");
  const [lastName, setLastName] = useState(u.lastName || "");
  const [email, setEmail] = useState(u.email || "");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  async function save() {
    setBusy(true); setErr(null); setMsg(null);
    try { await api.adminUpdateUser(u.id, { firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim() }); setMsg("Saved. The user's sessions were reset."); onDone(); }
    catch (e: any) { setErr(e?.message || "Could not save."); } finally { setBusy(false); }
  }
  async function resetPw() {
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    setBusy(true); setErr(null); setMsg(null);
    try { await api.adminResetPassword(u.id, pw); setPw(""); setMsg("Password reset — the user must sign in again."); }
    catch (e: any) { setErr(e?.message || "Could not reset password."); } finally { setBusy(false); }
  }
  async function clearReview() { setBusy(true); try { await api.adminClearIdentityReview(u.id); onDone(); } finally { setBusy(false); } }
  return (
    <div className="border-t border-line mt-3 pt-3 space-y-2" data-testid="admin-user-editor">
      <div className="grid sm:grid-cols-2 gap-2">
        <div><label className="label">First name</label><input className="field" value={firstName} onChange={(e) => setFirstName(e.target.value)} /></div>
        <div><label className="label">Last name</label><input className="field" value={lastName} onChange={(e) => setLastName(e.target.value)} /></div>
      </div>
      <div><label className="label">Email</label><input className="field" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
      <div className="flex items-end gap-2">
        <div className="flex-1"><label className="label">Reset password (min 8 chars)</label><input className="field" type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" /></div>
        <button onClick={resetPw} disabled={busy || pw.length < 8} className="btn-outline h-10 disabled:opacity-50" data-testid="admin-reset-pw">Reset password</button>
      </div>
      {u.identityReviewPending && <button onClick={clearReview} disabled={busy} className="btn-outline h-8 px-3 text-xs text-brand-green">Mark identity review OK</button>}
      {err && <p className="text-sm text-brand-red">{err}</p>}
      {msg && <p className="text-sm text-brand-green">{msg}</p>}
      <div className="flex gap-2">
        <button onClick={save} disabled={busy} className="btn-red h-9 disabled:opacity-60" data-testid="admin-save-user">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}Save changes</button>
        <button onClick={onDone} className="btn-outline h-9">Close</button>
      </div>
      <AdminUserWarnings userId={u.id} />
    </div>
  );
}

const WARN_REASONS = ["spam", "abuse", "policy_violation", "no_show", "payment_issue", "other"];
function AdminUserWarnings({ userId }: { userId: number }) {
  const [warnings, setWarnings] = useState<any[] | null>(null);
  const [reason, setReason] = useState("policy_violation");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() { try { setWarnings(await api.adminUserWarnings(userId)); } catch { setWarnings([]); } }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  async function issue() {
    setBusy(true); setErr(null);
    try { await api.adminWarnUser(userId, reason, note.trim() || undefined); setNote(""); await load(); }
    catch (e: any) { setErr(e?.message || "Could not issue warning."); } finally { setBusy(false); }
  }

  return (
    <div className="border-t border-line mt-3 pt-3">
      <div className="text-sm font-semibold text-navy mb-2 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 text-brand-gold" />Warnings {warnings && warnings.length > 0 && <span className="chip bg-brand-red/10 text-brand-red text-[10px]">{warnings.length}</span>}</div>
      {warnings && warnings.length > 0 && (
        <div className="space-y-1.5 mb-3 max-h-40 overflow-auto">
          {warnings.map((w) => (
            <div key={w.id} className="text-xs border border-line rounded-lg p-2">
              <span className="chip bg-brand-gold/10 text-[#8a5a00] text-[10px] mr-1 capitalize">{String(w.reason).replace(/_/g, " ")}</span>
              {w.note && <span className="text-navy-700">{w.note}</span>}
              <span className="text-mut ml-1">· {new Date(w.createdAt).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <div><label className="label">Reason</label>
          <select className="field h-9" value={reason} onChange={(e) => setReason(e.target.value)}>
            {WARN_REASONS.map((r) => <option key={r} value={r} className="capitalize">{r.replace(/_/g, " ")}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[160px]"><label className="label">Note (optional)</label><input className="field h-9" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Details for the user" /></div>
        <button onClick={issue} disabled={busy} className="btn-outline h-9 text-brand-red disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}Issue warning</button>
      </div>
      {err && <p className="text-xs text-brand-red mt-1">{err}</p>}
    </div>
  );
}

function AnalyticsTab() {
  const [d, setD] = useState<Awaited<ReturnType<typeof api.adminAnalytics>> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { api.adminAnalytics().then(setD).catch((e) => setErr(e?.message || "Could not load analytics.")); }, []);
  if (err) return <div className="card p-6 text-sm text-brand-red">{err}</div>;
  if (!d) return <Loading />;

  const maxBooking = Math.max(1, ...d.bookingsByMonth.map((b) => b.count));
  const maxRev = Math.max(1, ...d.revenueByMonth.map((r) => r.revenue));
  const dist = d.scoreDistribution || { A: 0, B: 0, C: 0 };
  const distTotal = Math.max(1, (dist.A || 0) + (dist.B || 0) + (dist.C || 0));
  const maxCat = Math.max(1, ...d.topCategories.map((c) => c.count));
  const tierColor: Record<string, string> = { A: "bg-emerald-500", B: "bg-amber-500", C: "bg-navy-400" };

  return (
    <div className="space-y-4">
      {/* Totals */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="card p-4"><div className="text-2xl font-extrabold text-navy">{peso(d.totals.revenue)}</div><div className="text-xs text-mut mt-0.5">Platform revenue (all-time fees)</div></div>
        <div className="card p-4"><div className="text-2xl font-extrabold text-navy">{peso(d.totals.gmv)}</div><div className="text-xs text-mut mt-0.5">Gross transaction value</div></div>
        <div className="card p-4"><div className="text-2xl font-extrabold text-navy">{d.bookingsByMonth.reduce((s, b) => s + b.count, 0)}</div><div className="text-xs text-mut mt-0.5">Contracts (last 6 months)</div></div>
        <div className="card p-4"><div className="text-2xl font-extrabold text-navy">{distTotal}</div><div className="text-xs text-mut mt-0.5">Scored workers</div></div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Bookings by month */}
        <div className="card p-5">
          <h3 className="font-bold text-navy mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4" />Bookings by month</h3>
          {d.bookingsByMonth.length === 0 ? <Empty text="No bookings in this window." /> : (
            <div className="flex items-end gap-2 h-40">
              {d.bookingsByMonth.map((b) => (
                <div key={b.month} className="flex-1 flex flex-col items-center justify-end gap-1">
                  <span className="text-[11px] font-semibold text-navy">{b.count}</span>
                  <div className="w-full bg-navy rounded-t" style={{ height: `${(b.count / maxBooking) * 100}%`, minHeight: b.count ? 4 : 0 }} />
                  <span className="text-[10px] text-mut">{b.month.slice(5)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Revenue by month */}
        <div className="card p-5">
          <h3 className="font-bold text-navy mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4" />Revenue by month</h3>
          {d.revenueByMonth.length === 0 ? <Empty text="No revenue in this window." /> : (
            <div className="flex items-end gap-2 h-40">
              {d.revenueByMonth.map((r) => (
                <div key={r.month} className="flex-1 flex flex-col items-center justify-end gap-1" title={`GMV ${peso(r.gmv)}`}>
                  <span className="text-[10px] font-semibold text-navy">{peso(r.revenue)}</span>
                  <div className="w-full bg-brand-green rounded-t" style={{ height: `${(r.revenue / maxRev) * 100}%`, minHeight: r.revenue ? 4 : 0 }} />
                  <span className="text-[10px] text-mut">{r.month.slice(5)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Score distribution */}
        <div className="card p-5">
          <h3 className="font-bold text-navy mb-3">Worker score distribution</h3>
          <div className="space-y-2">
            {(["A", "B", "C"] as const).map((t) => (
              <div key={t}>
                <div className="flex justify-between text-xs text-mut mb-0.5"><span>Tier {t}</span><span>{dist[t] || 0}</span></div>
                <div className="h-3 rounded-full bg-navy-50 overflow-hidden"><div className={`h-full ${tierColor[t]}`} style={{ width: `${((dist[t] || 0) / distTotal) * 100}%` }} /></div>
              </div>
            ))}
          </div>
        </div>

        {/* Top categories */}
        <div className="card p-5">
          <h3 className="font-bold text-navy mb-3">Top demand categories</h3>
          {d.topCategories.length === 0 ? <Empty text="No category data yet." /> : (
            <div className="space-y-2">
              {d.topCategories.map((c) => (
                <div key={c.category}>
                  <div className="flex justify-between text-xs text-mut mb-0.5"><span className="capitalize truncate">{c.category}</span><span>{c.count}</span></div>
                  <div className="h-2.5 rounded-full bg-navy-50 overflow-hidden"><div className="h-full bg-brand-gold" style={{ width: `${(c.count / maxCat) * 100}%` }} /></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const LISTING_TYPES = ["items", "services", "companies", "people", "jobs", "projects", "rfqs"];
function ListingsTab() {
  const [type, setType] = useState("items");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<any[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  async function load() { setRows(await api.adminListings(type, q).catch(() => [])); }
  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [type, q]);
  async function toggle(l: any) {
    setBusy(l.id);
    try { await api.adminModerate(type, l.id, l.hidden ? "restore" : "hide"); await load(); } catch { /* ignore */ }
    finally { setBusy(null); }
  }
  return (
    <div>
      <div className="flex gap-1 flex-wrap mb-3">
        {LISTING_TYPES.map((t) => (
          <button key={t} onClick={() => setType(t)} className={`chip capitalize ${type === t ? "bg-navy text-white" : "bg-navy-50 text-navy-600 hover:bg-navy hover:text-white"}`}>{t}</button>
        ))}
      </div>
      <SearchBox value={q} onChange={setQ} placeholder={`Search ${type}…`} />
      {!rows ? <Loading /> : (
        <div className="space-y-2 mt-3">
          {rows.length === 0 && <Empty text={`No ${type} found.`} />}
          {rows.map((l) => (
            <div key={l.id} className="card p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <Link href={l.detailPath} className="font-semibold text-navy hover:text-brand-red truncate block">{l.title}</Link>
                <div className="text-xs text-mut truncate">{l.owner ? `by ${l.owner}` : "—"}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {l.hidden && <span className="chip bg-brand-red/10 text-brand-red">Hidden</span>}
                <button onClick={() => toggle(l)} disabled={busy === l.id} className={`h-8 px-3 text-xs rounded-lg font-semibold disabled:opacity-50 ${l.hidden ? "btn-navy" : "btn-outline text-brand-red"}`}>
                  {l.hidden ? <><Eye className="w-3.5 h-3.5" />Restore</> : <><EyeOff className="w-3.5 h-3.5" />Hide</>}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewsTab() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<any[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  async function load() { setRows(await api.adminReviews(q).catch(() => [])); }
  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [q]);
  async function remove(id: string) {
    setBusy(id);
    try { await api.adminDeleteReview(id); await load(); } catch { /* ignore */ }
    finally { setBusy(null); }
  }
  return (
    <div>
      <SearchBox value={q} onChange={setQ} placeholder="Search reviews by text or name…" />
      {!rows ? <Loading /> : (
        <div className="space-y-2 mt-3">
          {rows.length === 0 && <Empty text="No reviews found." />}
          {rows.map((r) => (
            <div key={r.id} className="card p-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2"><span className="text-sm font-semibold text-navy">{r.raterName}</span><span className="chip bg-brand-gold/15 text-[#8a5a00]">{r.rating}★</span><span className="text-xs text-mut">→ {r.rateeName}</span></div>
                {r.comment && <p className="text-sm text-navy-700 mt-1">{r.comment}</p>}
                {r.contextLabel && <p className="text-xs text-mut">on “{r.contextLabel}”</p>}
              </div>
              <button onClick={() => remove(r.id)} disabled={busy === r.id} className="btn-outline h-8 px-3 text-xs text-brand-red shrink-0 disabled:opacity-50"><Trash2 className="w-3.5 h-3.5" />Remove</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TransactionsTab() {
  const [data, setData] = useState<any>(null);
  useEffect(() => { api.adminTransactions().then(setData).catch(() => setData({ orders: [], contracts: [] })); }, []);
  if (!data) return <Loading />;
  const chip = (s: string) => <span className="chip capitalize bg-navy-50 text-navy-600">{s}</span>;
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="card p-4">
        <h3 className="font-bold text-navy mb-3">Orders</h3>
        <div className="space-y-2">
          {data.orders.length === 0 && <Empty text="No orders yet." />}
          {data.orders.map((o: any) => (
            <div key={o.id} className="flex items-center justify-between border border-line rounded-lg p-2.5 gap-2">
              <div className="min-w-0"><div className="text-sm font-medium text-navy truncate">{o.itemTitle}</div><div className="text-xs text-mut truncate">{o.buyerName} → {o.sellerName}</div></div>
              <div className="text-right shrink-0"><div className="text-sm font-bold text-navy">{peso(o.total)}</div>{chip(o.status)}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="card p-4">
        <h3 className="font-bold text-navy mb-3">Contracts</h3>
        <div className="space-y-2">
          {data.contracts.length === 0 && <Empty text="No contracts yet." />}
          {data.contracts.map((c: any) => (
            <div key={c.id} className="flex items-center justify-between border border-line rounded-lg p-2.5 gap-2">
              <div className="min-w-0"><div className="text-sm font-medium text-navy truncate">{c.title}</div><div className="text-xs text-mut truncate">{c.buyerName} → {c.providerName}</div></div>
              <div className="text-right shrink-0"><div className="text-sm font-bold text-navy">{peso(c.amount)}</div>{chip(c.status)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const FLAG_REASON_LABELS: Record<string, string> = {
  spam: "Spam", scam: "Scam", abuse: "Abuse", duplicate: "Duplicate", payment_risk: "Payment risk",
  rapid_signup: "Rapid signup", keyword: "Risk keywords", manual: "Manual", other: "Other",
};
const SEV_STYLES: Record<string, string> = {
  high: "bg-red-50 text-brand-red", medium: "bg-amber-50 text-amber-700", low: "bg-navy-50 text-mut",
};

const TICKET_STATUS_STYLES: Record<string, string> = {
  open: "bg-amber-50 text-amber-700", in_progress: "bg-blue-50 text-blue-700", resolved: "bg-green-50 text-brand-green",
};
const ticketStatusLabel = (s: string) => (s === "in_progress" ? "In progress" : s.charAt(0).toUpperCase() + s.slice(1));

function JobsTab() {
  const [jobs, setJobs] = useState<any[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const fmtInterval = (ms: number) => (ms >= 86400000 ? `${Math.round(ms / 86400000)}d` : `${Math.round(ms / 3600000)}h`);
  const fmtSummary = (s: any) => (s && Object.keys(s).length ? Object.entries(s).map(([k, v]) => `${k}: ${v}`).join(", ") : "—");
  async function load() { setJobs(await api.adminJobs().catch(() => [])); }
  useEffect(() => { load(); }, []);
  async function run(key: string) {
    setBusy(key);
    try { await api.adminRunJob(key); await load(); }
    catch (e: any) { alert(e?.message || "Failed."); } finally { setBusy(null); }
  }
  if (!jobs) return <Loading />;
  return (
    <div>
      <p className="text-sm text-mut mb-4">Background jobs run on a schedule. Trigger any job manually to run it now; the last run's status and result are shown.</p>
      <div className="space-y-2">
        {jobs.map((j) => {
          const lr = j.lastRun;
          return (
            <div key={j.key} className="card p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-navy">{j.label}</span>
                    <span className="chip bg-navy-50 text-mut">every {fmtInterval(j.intervalMs)}</span>
                    {j.running && <span className="chip bg-blue-50 text-blue-700">running</span>}
                    {lr && lr.status === "ok" && <span className="chip bg-green-50 text-brand-green">ok</span>}
                    {lr && lr.status === "error" && <span className="chip bg-red-50 text-brand-red">error</span>}
                    {!lr && <span className="chip bg-navy-50 text-mut">never run</span>}
                  </div>
                  <p className="text-xs text-mut mt-1">{j.description}</p>
                  {lr && (
                    <p className="text-xs text-mut mt-1">
                      Last: {new Date(lr.startedAt).toLocaleString()} · {lr.trigger} · {lr.durationMs != null ? `${lr.durationMs}ms` : "—"}
                      {lr.status === "error" ? <span className="text-brand-red"> · {lr.error}</span> : <> · <span className="text-navy">{fmtSummary(lr.summary)}</span></>}
                    </p>
                  )}
                </div>
                <button onClick={() => run(j.key)} disabled={busy === j.key} className="btn-outline h-8 px-3 text-xs shrink-0 disabled:opacity-60" data-testid={`run-${j.key}`}>
                  {busy === j.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}Run now
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SupportTab() {
  const [status, setStatus] = useState("open");
  const [rows, setRows] = useState<any[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  async function load() { setRows(null); setRows(await api.adminTickets(status).catch(() => [])); }
  useEffect(() => { load(); }, [status]);

  if (openId) return <AdminTicketThread id={openId} onBack={() => { setOpenId(null); load(); }} />;

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["open", "in_progress", "resolved", "all"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`h-8 px-3 rounded-lg text-xs font-semibold ${status === s ? "bg-brand-red text-white" : "bg-navy-50 text-mut hover:text-navy"}`}>{s === "in_progress" ? "In progress" : s.charAt(0).toUpperCase() + s.slice(1)}</button>
        ))}
      </div>
      {!rows ? <Loading /> : rows.length === 0 ? <Empty text={`No ${status === "all" ? "" : ticketStatusLabel(status).toLowerCase() + " "}tickets.`} /> : (
        <div className="space-y-2">
          {rows.map((t) => (
            <button key={t.id} onClick={() => setOpenId(t.id)} className="card p-3 w-full text-left hover:border-navy-500 transition flex items-center justify-between gap-3" data-testid="admin-ticket-row">
              <div className="min-w-0">
                <div className="font-semibold text-navy truncate">{t.subject}</div>
                <div className="text-xs text-mut">{t.userName || "—"} · {t.category} · {new Date(t.createdAt).toLocaleDateString()}</div>
              </div>
              <span className={`chip shrink-0 ${TICKET_STATUS_STYLES[t.status] || ""}`}>{ticketStatusLabel(t.status)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminTicketThread({ id, onBack }: { id: string; onBack: () => void }) {
  const [data, setData] = useState<any | null>(null);
  const [reply, setReply] = useState("");
  const [outcome, setOutcome] = useState("");
  const [busy, setBusy] = useState(false);
  async function load() { setData(await api.adminTicket(id).catch(() => null)); }
  useEffect(() => { load(); }, [id]);
  async function send() {
    if (!reply.trim()) return;
    setBusy(true);
    try { await api.adminReplyTicket(id, reply.trim()); setReply(""); await load(); }
    catch (e: any) { alert(e?.message || "Failed."); } finally { setBusy(false); }
  }
  async function resolve() {
    setBusy(true);
    try { await api.adminSetTicketStatus(id, "resolved", outcome.trim() || undefined); await load(); }
    catch (e: any) { alert(e?.message || "Failed."); } finally { setBusy(false); }
  }
  if (!data) return <Loading />;
  const t = data.ticket;
  return (
    <div>
      <button onClick={onBack} className="text-sm text-mut hover:text-navy flex items-center gap-1 mb-3"><ChevronLeft className="w-4 h-4" />Back to tickets</button>
      <div className="flex items-center justify-between gap-3 mb-1">
        <h3 className="text-lg font-bold text-navy">{t.subject}</h3>
        <span className={`chip shrink-0 ${TICKET_STATUS_STYLES[t.status] || ""}`}>{ticketStatusLabel(t.status)}</span>
      </div>
      <p className="text-xs text-mut mb-4">{t.category} · opened {new Date(t.createdAt).toLocaleDateString()}</p>
      <div className="space-y-2" data-testid="admin-ticket-thread">
        {data.messages.map((m: any) => (
          <div key={m.id} className={`card p-3 ${m.byRole === "admin" ? "bg-navy-50/60" : ""}`}>
            <div className="text-xs text-mut mb-1">{m.byRole === "admin" ? "Support team" : "Member"} · {new Date(m.createdAt).toLocaleString()}</div>
            <p className="text-sm text-navy whitespace-pre-line">{m.body}</p>
          </div>
        ))}
      </div>
      {t.status !== "resolved" && (
        <div className="mt-4 space-y-3">
          <div className="flex items-end gap-2">
            <div className="flex-1"><label className="label">Reply to member</label><textarea className="field" rows={2} value={reply} onChange={(e) => setReply(e.target.value)} /></div>
            <button onClick={send} disabled={busy} className="btn-navy h-10 disabled:opacity-60" data-testid="admin-reply-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Send</button>
          </div>
          <div className="card p-3">
            <label className="label">Resolution / outcome (sent to member)</label>
            <textarea className="field" rows={2} value={outcome} onChange={(e) => setOutcome(e.target.value)} placeholder="Summary of how this was resolved" />
            <button onClick={resolve} disabled={busy} className="btn-red h-9 mt-2 disabled:opacity-60" data-testid="admin-resolve-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}Mark resolved &amp; notify</button>
          </div>
        </div>
      )}
      {t.status === "resolved" && t.outcome && <div className="card p-3 mt-4 bg-green-50/50"><p className="text-sm text-navy"><span className="font-semibold">Resolution: </span>{t.outcome}</p></div>}
    </div>
  );
}

function FlagsTab() {
  const [status, setStatus] = useState("open");
  const [rows, setRows] = useState<any[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  async function load() { setRows(null); setRows(await api.adminFlags(status).catch(() => [])); }
  useEffect(() => { load(); }, [status]);

  async function resolve(f: any, resolution: "dismissed" | "content_hidden" | "user_suspended" | "no_action") {
    const labels: Record<string, string> = { dismissed: "dismiss", content_hidden: "hide the flagged content", user_suspended: "suspend the user", no_action: "close with no action" };
    if (!confirm(`Resolve this flag — ${labels[resolution]}?`)) return;
    setBusy(f.id);
    try { const r = await api.adminResolveFlag(f.id, resolution); if (r?.applied) { /* action applied */ } await load(); }
    catch (e: any) { alert(e?.message || "Could not resolve."); } finally { setBusy(null); }
  }

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["open", "resolved", "dismissed", "all"].map((s) => (
          <button key={s} onClick={() => setStatus(s)}
            className={`h-8 px-3 rounded-lg text-xs font-semibold capitalize ${status === s ? "bg-brand-red text-white" : "bg-navy-50 text-mut hover:text-navy"}`}>{s}</button>
        ))}
      </div>
      {!rows ? <Loading /> : rows.length === 0 ? <Empty text={`No ${status === "all" ? "" : status + " "}flags.`} /> : (
        <div className="space-y-2">
          {rows.map((f) => (
            <div key={f.id} className="card p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`chip ${SEV_STYLES[f.severity] || SEV_STYLES.low}`}>{f.severity}</span>
                    <span className="font-semibold text-navy">{FLAG_REASON_LABELS[f.reason] || f.reason}</span>
                    <span className="text-xs text-mut">{f.source === "auto" ? "auto" : "reported"}</span>
                    <span className="chip bg-navy-50 text-mut">{f.targetType}</span>
                  </div>
                  {f.detail && <p className="text-sm text-navy mt-1">{f.detail}</p>}
                  <p className="text-xs text-mut mt-1">
                    Target: <span className="font-mono">{f.targetId}</span>
                    {f.targetUserName && <> · owner {f.targetUserName}{f.targetUserStatus === "suspended" && <span className="text-brand-red"> (suspended)</span>}</>}
                  </p>
                  {f.status !== "open" && (
                    <p className="text-xs text-mut mt-1">
                      {f.status === "dismissed" ? "Dismissed" : "Resolved"}{f.resolution ? ` — ${f.resolution.replace(/_/g, " ")}` : ""}{f.resolutionNote ? `: ${f.resolutionNote}` : ""}
                    </p>
                  )}
                </div>
              </div>
              {f.status === "open" && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {(f.targetType !== "user") && <button disabled={busy === f.id} onClick={() => resolve(f, "content_hidden")} className="btn-outline h-8 px-3 text-xs disabled:opacity-60"><EyeOff className="w-3.5 h-3.5" />Hide content</button>}
                  {(f.targetUserId || f.targetType === "user") && <button disabled={busy === f.id} onClick={() => resolve(f, "user_suspended")} className="btn-outline h-8 px-3 text-xs text-brand-red disabled:opacity-60"><Ban className="w-3.5 h-3.5" />Suspend user</button>}
                  <button disabled={busy === f.id} onClick={() => resolve(f, "no_action")} className="btn-outline h-8 px-3 text-xs disabled:opacity-60"><Check className="w-3.5 h-3.5" />No action</button>
                  <button disabled={busy === f.id} onClick={() => resolve(f, "dismissed")} className="btn-outline h-8 px-3 text-xs disabled:opacity-60"><X className="w-3.5 h-3.5" />Dismiss</button>
                  {busy === f.id && <Loader2 className="w-4 h-4 animate-spin text-mut self-center" />}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-mut mt-3">Auto-flags come from content risk scans &amp; signup heuristics; reported flags come from members. Resolving with an action moderates the target.</p>
    </div>
  );
}

function LegalTab() {
  const [data, setData] = useState<any | null>(null);
  const [kind, setKind] = useState<"terms" | "privacy">("terms");
  const [title, setTitle] = useState("Terms of Service");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  async function load() { setData(await api.adminLegal().catch(() => ({ current: {}, documents: [] }))); }
  useEffect(() => { load(); }, []);
  function pickKind(k: "terms" | "privacy") { setKind(k); setTitle(k === "terms" ? "Terms of Service" : "Privacy Policy"); }
  async function publish() {
    if (body.trim().length < 10) { setMsg("Document body is too short."); return; }
    setBusy(true); setMsg(null);
    try {
      const r = await api.adminPublishLegal({ kind, title: title.trim(), body, publish: true });
      setMsg(`Published ${kind} v${r?.document?.version}. Users will be prompted to re-accept.`);
      setBody(""); await load();
    } catch (e: any) { setMsg(e?.message || "Could not publish."); } finally { setBusy(false); }
  }
  if (!data) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="card p-4">
        <h3 className="font-bold text-navy mb-3">Publish a new version</h3>
        <div className="flex gap-2 mb-3">
          {(["terms", "privacy"] as const).map((k) => (
            <button key={k} onClick={() => pickKind(k)} className={`h-8 px-3 rounded-lg text-xs font-semibold capitalize ${kind === k ? "bg-brand-red text-white" : "bg-navy-50 text-mut hover:text-navy"}`}>{k === "terms" ? "Terms of Service" : "Privacy Policy"}</button>
          ))}
        </div>
        <label className="label">Title</label>
        <input className="field mb-2" value={title} onChange={(e) => setTitle(e.target.value)} />
        <label className="label">Body (Markdown-ish: use # and ## for headings)</label>
        <textarea className="field font-mono text-xs" rows={8} placeholder="# Title…" value={body} onChange={(e) => setBody(e.target.value)} />
        {msg && <p className="text-sm text-navy mt-2">{msg}</p>}
        <button onClick={publish} disabled={busy} className="btn-red h-10 mt-3 disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}Publish new version</button>
        <p className="text-xs text-mut mt-2">Publishing a new version bumps the current version and forces all members to re-accept before their next action.</p>
      </div>
      <div>
        <h3 className="font-bold text-navy mb-2">Versions</h3>
        {data.documents.length === 0 ? <Empty text="No legal documents yet." /> : (
          <div className="space-y-2">
            {data.documents.map((d: any) => (
              <div key={d.id} className="card p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <span className="font-semibold text-navy capitalize">{d.kind}</span>
                  <span className="text-xs text-mut ml-2">v{d.version} · {d.title}</span>
                  {d.isCurrent && <span className="chip bg-green-50 text-brand-green ml-2">Current</span>}
                  {!d.published && <span className="chip bg-navy-50 text-mut ml-2">Draft</span>}
                </div>
                <a href={`/legal/${d.kind}`} target="_blank" rel="noreferrer" className="btn-outline h-8 px-3 text-xs shrink-0"><Eye className="w-3.5 h-3.5" />View</a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CategoriesTab() {
  const [rows, setRows] = useState<any[] | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function load() { setRows(await api.adminCategories().catch(() => [])); }
  useEffect(() => { load(); }, []);
  async function add() {
    if (name.trim().length < 2) return;
    setBusy(true); setErr(null);
    try { await api.adminAddCategory(name.trim(), "general"); setName(""); await load(); }
    catch (e: any) { setErr(e?.message || "Could not add."); } finally { setBusy(false); }
  }
  async function toggle(c: any) { try { await api.adminUpdateCategory(c.id, { active: !c.active }); await load(); } catch (e: any) { alert(e?.message || "Failed."); } }
  async function remove(c: any) { try { await api.adminDeleteCategory(c.id); await load(); } catch (e: any) { alert(e?.message || "Failed."); } }
  if (!rows) return <Loading />;
  return (
    <div>
      <div className="card p-4 mb-4 flex items-end gap-2">
        <div className="flex-1"><label className="label">New category</label><input className="field" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Agriculture" onKeyDown={(e) => e.key === "Enter" && add()} /></div>
        <button onClick={add} disabled={busy} className="btn-red h-10 disabled:opacity-60"><Plus className="w-4 h-4" />Add</button>
      </div>
      {err && <p className="text-sm text-brand-red mb-2">{err}</p>}
      {rows.length === 0 ? <Empty text="No categories yet." /> : (
        <div className="space-y-2">
          {rows.map((c) => (
            <div key={c.id} className="card p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <span className="font-semibold text-navy">{c.name}</span>
                <span className="text-xs text-mut ml-2">{c.slug} · {c.group}</span>
                {!c.active && <span className="chip bg-navy-50 text-mut ml-2">Hidden</span>}
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => toggle(c)} className="btn-outline h-8 px-3 text-xs">{c.active ? <><EyeOff className="w-3.5 h-3.5" />Hide</> : <><Eye className="w-3.5 h-3.5" />Show</>}</button>
                <button onClick={() => remove(c)} className="btn-outline h-8 px-3 text-xs text-brand-red"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-mut mt-3">Active categories appear in the post-a-listing category picker.</p>
    </div>
  );
}

function FeesTab() {
  const [data, setData] = useState<any>(null);
  const [pct, setPct] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  async function load() { const d = await api.adminFees().catch(() => null); setData(d); if (d) setPct(String(d.feePct)); }
  useEffect(() => { load(); }, []);
  async function save() {
    setBusy(true); setMsg(null);
    try { const r = await api.adminSetFee(Number(pct)); setMsg(`Platform fee set to ${r?.feePct}%.`); await load(); }
    catch (e: any) { setMsg(e?.message || "Could not save."); } finally { setBusy(false); }
  }
  if (!data) return <Loading />;
  const peso = (n: number) => "₱" + (Number(n) || 0).toLocaleString("en-PH");
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="card p-5">
          <div className="text-xs text-mut">Current platform fee</div>
          <div className="text-3xl font-extrabold text-navy">{data.feePct}%</div>
          <div className="flex items-end gap-2 mt-3">
            <div className="flex-1"><label className="label">New fee %</label><input className="field" type="number" min="0" max="100" step="0.5" value={pct} onChange={(e) => setPct(e.target.value)} /></div>
            <button onClick={save} disabled={busy} className="btn-red h-10 disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</button>
          </div>
          <p className="text-xs text-mut mt-2">Applied to provider payouts (order sales &amp; milestone releases). 0% = no fee.</p>
          {msg && <p className="text-sm text-brand-green mt-2">{msg}</p>}
        </div>
        <div className="card p-5">
          <div className="text-xs text-mut">Total fees collected</div>
          <div className="text-3xl font-extrabold text-navy">{peso(data.totalCollected)}</div>
        </div>
      </div>
      <div className="card p-5">
        <h3 className="font-bold text-navy mb-3">Recent fees</h3>
        {data.recent.length === 0 ? <p className="text-sm text-mut">No fees collected yet.</p> : (
          <div className="space-y-2">
            {data.recent.map((r: any) => (
              <div key={r.id} className="flex items-center justify-between text-sm border-b border-line pb-2">
                <div><span className="chip bg-navy-50 text-navy-600 capitalize mr-2">{r.sourceType}</span><span className="text-mut">{r.sourceId}</span></div>
                <div className="text-right"><div className="font-semibold text-navy">{peso(r.fee)} <span className="text-xs text-mut">({r.feePct}% of {peso(r.gross)})</span></div></div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function WorkerKycTab() {
  const [rows, setRows] = useState<any[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [docUrls, setDocUrls] = useState<Record<string, string>>({});
  async function load() { setRows(await api.adminWorkerVerifications("pending").catch(() => [])); }
  useEffect(() => { load(); }, []);
  async function viewDoc(vid: string, doc: "id_image" | "selfie" | "address_proof") {
    const key = `${vid}:${doc}`;
    if (docUrls[key]) { setDocUrls((s) => ({ ...s, [key]: "" })); return; } // toggle off
    const url = await api.adminKycDocUrl(vid, doc);
    if (url) setDocUrls((s) => ({ ...s, [key]: url }));
  }
  async function approve(id: string) { setBusy(id + "a"); try { await api.adminApproveWorkerVerification(id); await load(); } catch (e: any) { alert(e?.message || "Could not approve."); } finally { setBusy(null); } }
  async function reject(id: string) { setBusy(id + "r"); try { await api.adminRejectWorkerVerification(id, note.trim() || undefined); setRejectId(null); setNote(""); await load(); } catch (e: any) { alert(e?.message || "Could not reject."); } finally { setBusy(null); } }
  if (!rows) return <Loading />;
  if (rows.length === 0) return <Empty text="No worker verifications waiting for review." />;
  const DOCS: ["id_image" | "selfie" | "address_proof", string][] = [["id_image", "ID"], ["selfie", "Selfie"], ["address_proof", "Address"]];
  return (
    <div className="space-y-3">
      {rows.map((v) => (
        <div key={v.id} className="card p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-navy-50 text-navy grid place-items-center shrink-0"><IdCard className="w-5 h-5" /></div>
              <div className="min-w-0">
                <Link href={`/u/${v.userId}`} className="font-bold text-navy hover:text-brand-red">{v.userName}</Link>
                <div className="text-xs text-mut">{v.email}</div>
                <div className="text-sm text-navy-700 mt-1"><span className="chip bg-navy-50 text-navy-600 capitalize mr-2">{(v.idType || "ID").replace(/_/g, " ")}</span>{v.idNumber || <span className="text-mut">No number</span>}</div>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => approve(v.id)} disabled={busy === v.id + "a"} className="btn-navy h-9 px-3 text-xs disabled:opacity-50"><Check className="w-3.5 h-3.5" />Approve</button>
              <button onClick={() => { setRejectId(rejectId === v.id ? null : v.id); setNote(""); }} className="btn-outline h-9 px-3 text-xs text-brand-red"><X className="w-3.5 h-3.5" />Reject</button>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-line flex flex-wrap gap-2">
            {DOCS.map(([doc, label]) => (
              <button key={doc} onClick={() => viewDoc(v.id, doc)} disabled={!v.docs?.[doc]} className="btn-outline h-8 px-3 text-xs disabled:opacity-40">
                <Eye className="w-3.5 h-3.5" />{label}{v.docs?.[doc] ? "" : " (none)"}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-2">
            {DOCS.map(([doc]) => {
              const url = docUrls[`${v.id}:${doc}`];
              return url ? <img key={doc} src={url} alt={doc} className="max-h-48 rounded-lg border border-line" /> : null;
            })}
          </div>
          {rejectId === v.id && (
            <div className="mt-3 pt-3 border-t border-line flex gap-2">
              <input className="field flex-1" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason shown to the worker (e.g. blurry selfie)" />
              <button onClick={() => reject(v.id)} disabled={busy === v.id + "r"} className="btn-red h-10 px-3 text-xs disabled:opacity-50">Confirm reject</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function WorkerCredsTab() {
  const [rows, setRows] = useState<any[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [docUrls, setDocUrls] = useState<Record<string, string>>({});
  async function load() { setRows(await api.adminWorkerCredentials("pending").catch(() => [])); }
  useEffect(() => { load(); }, []);
  async function viewDoc(id: string) {
    if (docUrls[id]) { setDocUrls((s) => ({ ...s, [id]: "" })); return; }
    const url = await api.adminCredentialDocUrl(id);
    if (url) setDocUrls((s) => ({ ...s, [id]: url }));
  }
  async function approve(id: string) { setBusy(id + "a"); try { await api.adminApproveCredential(id); await load(); } catch (e: any) { alert(e?.message || "Could not approve."); } finally { setBusy(null); } }
  async function reject(id: string) { setBusy(id + "r"); try { await api.adminRejectCredential(id, note.trim() || undefined); setRejectId(null); setNote(""); await load(); } catch (e: any) { alert(e?.message || "Could not reject."); } finally { setBusy(null); } }
  if (!rows) return <Loading />;
  if (rows.length === 0) return <Empty text="No credentials waiting for review." />;
  return (
    <div className="space-y-3">
      {rows.map((c) => (
        <div key={c.id} className="card p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-navy-50 text-navy grid place-items-center shrink-0"><BadgeCheck className="w-5 h-5" /></div>
              <div className="min-w-0">
                <Link href={`/u/${c.userId}`} className="font-bold text-navy hover:text-brand-red">{c.userName}</Link>
                <div className="text-xs text-mut">{c.email}</div>
                <div className="text-sm text-navy-700 mt-1"><span className="font-semibold">{c.credentialType}</span>{c.credentialNumber ? ` · ${c.credentialNumber}` : ""}{c.issuer ? ` · ${c.issuer}` : ""}</div>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              {c.hasDocument && <button onClick={() => viewDoc(c.id)} className="btn-outline h-9 px-3 text-xs"><Eye className="w-3.5 h-3.5" />Doc</button>}
              <button onClick={() => approve(c.id)} disabled={busy === c.id + "a"} className="btn-navy h-9 px-3 text-xs disabled:opacity-50"><Check className="w-3.5 h-3.5" />Approve</button>
              <button onClick={() => { setRejectId(rejectId === c.id ? null : c.id); setNote(""); }} className="btn-outline h-9 px-3 text-xs text-brand-red"><X className="w-3.5 h-3.5" />Reject</button>
            </div>
          </div>
          {docUrls[c.id] && <img src={docUrls[c.id]} alt="credential" className="max-h-48 rounded-lg border border-line mt-3" />}
          {rejectId === c.id && (
            <div className="mt-3 pt-3 border-t border-line flex gap-2">
              <input className="field flex-1" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason shown to the worker" />
              <button onClick={() => reject(c.id)} disabled={busy === c.id + "r"} className="btn-red h-10 px-3 text-xs disabled:opacity-50">Confirm reject</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function VerificationsTab() {
  const [pending, setPending] = useState<any[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [note, setNote] = useState("");
  async function load() { setPending(await api.pendingVerifications().catch(() => [])); }
  useEffect(() => { load(); }, []);
  async function approve(id: string) { setBusy(id + "a"); try { await api.approveVerification(id); await load(); } catch { /* */ } finally { setBusy(null); } }
  async function reject(id: string) { setBusy(id + "r"); try { await api.rejectVerification(id, note.trim() || undefined); setRejectId(null); setNote(""); await load(); } catch { /* */ } finally { setBusy(null); } }
  if (!pending) return <Loading />;
  if (pending.length === 0) return <Empty text="No pending verification requests." />;
  return (
    <div className="space-y-3">
      {pending.map((p) => (
        <div key={p.id} className="card p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-navy-50 text-navy grid place-items-center shrink-0">{p.kind === "company" ? <Building2 className="w-5 h-5" /> : <IdCard className="w-5 h-5" />}</div>
              <div className="min-w-0">
                <Link href={`/u/${p.userId}`} className="font-bold text-navy hover:text-brand-red">{p.userName}</Link>
                <div className="text-xs text-mut">{p.email}</div>
                <div className="text-sm text-navy-700 mt-1"><span className="chip bg-navy-50 text-navy-600 capitalize mr-2">{p.kind === "company" ? "Company" : "Identity"}</span>{p.label || <span className="text-mut">No label</span>}</div>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => approve(p.id)} disabled={busy === p.id + "a"} className="btn-navy h-9 px-3 text-xs disabled:opacity-50"><Check className="w-3.5 h-3.5" />Approve</button>
              <button onClick={() => { setRejectId(rejectId === p.id ? null : p.id); setNote(""); }} className="btn-outline h-9 px-3 text-xs text-brand-red"><X className="w-3.5 h-3.5" />Reject</button>
            </div>
          </div>
          {rejectId === p.id && (
            <div className="mt-3 pt-3 border-t border-line flex gap-2">
              <input className="field flex-1" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason (optional) — shown to the user" />
              <button onClick={() => reject(p.id)} disabled={busy === p.id + "r"} className="btn-red h-10 px-3 text-xs disabled:opacity-50">Confirm reject</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function DisputesTab() {
  const [rows, setRows] = useState<any[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [splitFor, setSplitFor] = useState<string | null>(null);
  const [refund, setRefund] = useState("");
  const [note, setNote] = useState("");
  async function load() { setRows(await api.adminDisputeQueue().catch(() => [])); }
  useEffect(() => { load(); }, []);
  async function resolve(d: any, resolution: "refund" | "release" | "split") {
    setBusy(d.id + resolution);
    try {
      if (resolution === "split") {
        const rf = Number(String(refund).replace(/[^\d]/g, "")) || 0;
        await api.resolveDispute(d.id, { resolution, refundAmount: rf, releaseAmount: d.amount - rf, note: note.trim() || undefined });
      } else {
        await api.resolveDispute(d.id, { resolution, note: note.trim() || undefined });
      }
      setSplitFor(null); setRefund(""); setNote(""); await load();
    } catch (e: any) { alert(e?.message || "Could not resolve."); }
    finally { setBusy(null); }
  }
  if (!rows) return <Loading />;
  if (rows.length === 0) return <Empty text="No open disputes." />;
  return (
    <div className="space-y-3">
      {rows.map((d) => (
        <div key={d.id} className="card p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-navy">{d.contextLabel || d.dealType}</span>
                <span className="chip bg-brand-gold/15 text-[#8a5a00] capitalize">{d.status}</span>
                <span className="chip bg-navy-50 text-navy-600">{REASON_LABELS[d.reason] || d.reason}</span>
                <span className="chip bg-navy-50 text-navy-600">{peso(d.amount)}</span>
              </div>
              <div className="text-xs text-mut mt-1">Opened by {d.openedByName} ({d.openedByRole}) · {d.dealType}</div>
              {d.message && <p className="text-sm text-navy-700 mt-2">Claim: “{d.message}”</p>}
              {d.response && <p className="text-sm text-mut mt-1">Response: “{d.response}”</p>}
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-line">
            <input className="field mb-2" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Resolution note (optional) — shown to both parties" />
            <div className="flex flex-wrap gap-2 items-center">
              <button onClick={() => resolve(d, "refund")} disabled={!!busy} className="btn-outline h-9 px-3 text-xs text-brand-red disabled:opacity-50">Refund buyer ({peso(d.amount)})</button>
              <button onClick={() => resolve(d, "release")} disabled={!!busy} className="btn-navy h-9 px-3 text-xs disabled:opacity-50">Release {d.dealType === "order" ? "to seller" : "to provider"}</button>
              {d.dealType === "order" && (
                splitFor === d.id ? (
                  <div className="flex items-center gap-2">
                    <input className="field h-9 w-28 text-xs" value={refund} onChange={(e) => setRefund(e.target.value)} placeholder="refund ₱" inputMode="numeric" />
                    <span className="text-xs text-mut">buyer / seller gets {peso(Math.max(0, d.amount - (Number(String(refund).replace(/[^\d]/g, "")) || 0)))}</span>
                    <button onClick={() => resolve(d, "split")} disabled={!!busy} className="btn-red h-9 px-3 text-xs disabled:opacity-50">Confirm split</button>
                  </div>
                ) : (
                  <button onClick={() => { setSplitFor(d.id); setRefund(""); }} className="btn-outline h-9 px-3 text-xs">Split…</button>
                )
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function CreditTab() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<any[] | null>(null);
  const [edit, setEdit] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState<number | null>(null);
  async function load() { setRows(await api.adminCredit(q).catch(() => [])); }
  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [q]);
  async function setLimit(a: any) {
    const v = Number(String(edit[a.id] ?? a.limit).replace(/[^\d]/g, "")) || 0;
    setBusy(a.id);
    try { await api.adminSetCreditLimit(a.id, v); await load(); } catch { /* ignore */ } finally { setBusy(null); }
  }
  return (
    <div>
      <p className="text-sm text-mut mb-3">Grant a credit limit to any account. Buyers can then check out Materials orders on credit and repay from their wallet.</p>
      <SearchBox value={q} onChange={setQ} placeholder="Search accounts by name or email…" />
      {!rows ? <Loading /> : (
        <div className="space-y-2 mt-3">
          {rows.length === 0 && <Empty text="No accounts match." />}
          {rows.map((a) => (
            <div key={a.id} className="card p-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <Link href={`/u/${a.id}`} className="font-semibold text-navy hover:text-brand-red">{a.name || a.email}</Link>
                <div className="text-xs text-mut truncate">{a.email} · outstanding {peso(a.outstanding)} · available {peso(a.available)}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-mut">Limit ₱</span>
                <input className="field h-9 w-28 text-sm" inputMode="numeric" value={edit[a.id] ?? String(a.limit)} onChange={(e) => setEdit((m) => ({ ...m, [a.id]: e.target.value }))} />
                <button onClick={() => setLimit(a)} disabled={busy === a.id} className="btn-navy h-9 px-3 text-xs disabled:opacity-50">Save</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* shared bits */
function Loading() { return <div className="py-14 text-center text-mut"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>; }
function Empty({ text }: { text: string }) { return <div className="card p-8 text-center text-sm text-mut">{text}</div>; }
function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="relative max-w-md"><Search className="w-4 h-4 absolute left-3 top-3 text-mut" /><input className="field pl-9" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} /></div>
  );
}

/* ============================================================================
 * v3.2.0 — New admin portal sections
 * ========================================================================== */
function KpiTile({ label, value, sub, tone, hero }: { label: string; value: any; sub?: any; tone?: string; hero?: boolean }) {
  return (
    <div className={`rounded-xl p-4 border ${hero ? "bg-navy border-navy text-white" : "bg-white border-line"}`}>
      <div className={`text-[11px] font-semibold uppercase tracking-wide ${hero ? "text-white/70" : "text-mut"}`}>{label}</div>
      <div className={`text-2xl font-extrabold mt-1.5 ${hero ? "text-white" : tone || "text-navy"}`}>{value}</div>
      {sub != null && <div className={`text-[11.5px] mt-1 ${hero ? "text-white/70" : "text-mut"}`}>{sub}</div>}
    </div>
  );
}
function StripStat({ n, l, accent }: { n: any; l: string; accent?: string }) {
  return <div className={`bg-white border border-line rounded-xl p-3 ${accent || ""}`}><div className="text-xl font-extrabold text-navy">{n}</div><div className="text-[11px] text-mut uppercase tracking-wide mt-0.5">{l}</div></div>;
}
function Pill({ kind, children }: { kind: "ok" | "off" | "pend" | "ver"; children: any }) {
  const map: Record<string, string> = { ok: "bg-brand-green/10 text-brand-greendark", off: "bg-navy-50 text-mut", pend: "bg-brand-gold/15 text-[#8a5a00]", ver: "bg-blue-50 text-blue-700" };
  return <span className={`chip ${map[kind]}`}>{children}</span>;
}
const initials = (f?: string, l?: string) => `${(f?.[0] || "").toUpperCase()}${(l?.[0] || "").toUpperCase()}` || "?";

function DashboardSection({ go }: { go: (k: string) => void }) {
  const [d, setD] = useState<any>(null);
  const [a, setA] = useState<any>(null);
  useEffect(() => { api.adminDashboard().then(setD).catch(() => setD(null)); api.adminAnalytics().then(setA).catch(() => setA(null)); }, []);
  if (!d) return <Loading />;
  const rev = a?.revenueByMonth || [];
  const maxRev = Math.max(1, ...rev.map((r: any) => r.revenue));
  const attention = [
    { n: d.workers?.pendingKyc || 0, label: "Pending KYC reviews", to: "customers" },
    { n: d.pendingVerifications || 0, label: "Company / identity verifications", to: "verifications" },
    { n: d.openDisputes || 0, label: "Open disputes", to: "disputes" },
    { n: d.openFlags || 0, label: "Flagged content", to: "flags" },
    { n: d.outboxFailed || 0, label: "Failed emails (outbox)", to: "outbox" },
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile hero label="Funds in escrow" value={peso(d.escrow)} sub="Committed in milestones" />
        <KpiTile label="Total users" value={(d.users?.total || 0).toLocaleString()} sub={`${d.users?.active || 0} active`} />
        <KpiTile label="Active workers" value={(d.workers?.active || 0).toLocaleString()} sub={`of ${d.workers?.total || 0} registered`} />
        <KpiTile label="Pending KYC" value={d.workers?.pendingKyc || 0} tone="text-brand-gold" sub="Awaiting review" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile label="Revenue (MTD)" value={peso(d.finance?.revenueMTD)} sub="Platform fees this month" />
        <KpiTile label="GMV (MTD)" value={peso(d.finance?.gmvMTD)} />
        <KpiTile label="Open disputes" value={d.openDisputes || 0} tone={d.openDisputes ? "text-brand-red" : "text-navy"} />
        <KpiTile label="Cash-outs today" value={peso(d.finance?.cashOutsToday)} />
      </div>
      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-4">
        <div className="card p-5">
          <h3 className="font-bold text-navy mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4" />Revenue by month</h3>
          {rev.length === 0 ? <Empty text="No revenue in this window." /> : (
            <div className="flex items-end gap-2 h-40">
              {rev.map((r: any) => (
                <div key={r.month} className="flex-1 flex flex-col items-center justify-end gap-1" title={`GMV ${peso(r.gmv)}`}>
                  <span className="text-[10px] font-semibold text-navy">{peso(r.revenue)}</span>
                  <div className="w-full bg-brand-green rounded-t" style={{ height: `${(r.revenue / maxRev) * 100}%`, minHeight: r.revenue ? 4 : 0 }} />
                  <span className="text-[10px] text-mut">{String(r.month).slice(5)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="card p-5">
          <h3 className="font-bold text-navy mb-3">Needs attention</h3>
          <div className="space-y-2">
            {attention.map((x) => (
              <button key={x.label} onClick={() => go(x.to)} className="w-full flex items-center gap-3 text-left text-sm hover:bg-navy-50 rounded-lg px-2 py-1.5">
                <span className={`chip ${x.n ? "bg-brand-red/10 text-brand-red" : "bg-navy-50 text-mut"} min-w-[2rem] justify-center`}>{x.n}</span>
                <span className="text-navy-700">{x.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function CustomersSection() {
  const [q, setQ] = useState(""); const [type, setType] = useState(""); const [status, setStatus] = useState(""); const [kyc, setKyc] = useState("");
  const [data, setData] = useState<any>(null); const [openId, setOpenId] = useState<number | null>(null); const [openKind, setOpenKind] = useState<"client" | "worker">("worker");
  async function load() { setData(await api.adminCustomers({ q, type, status, kyc }).catch(() => ({ stats: {}, rows: [] }))); }
  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [q, type, status, kyc]);
  if (openId) return <MemberDetail id={openId} kind={openKind} onBack={() => { setOpenId(null); load(); }} />;
  const s = data?.stats || {};
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <StripStat n={s.total ?? "—"} l="Total customers" accent="border-l-4 border-l-navy" />
        <StripStat n={s.clients ?? "—"} l="Clients" accent="border-l-4 border-l-blue-500" />
        <StripStat n={s.workers ?? "—"} l="Workers" accent="border-l-4 border-l-brand-green" />
        <StripStat n={s.pendingKyc ?? "—"} l="Pending KYC" accent="border-l-4 border-l-brand-gold" />
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        <div className="flex-1 min-w-[200px]"><SearchBox value={q} onChange={setQ} placeholder="Search by name, email…" /></div>
        <select className="field h-10 w-auto" value={type} onChange={(e) => setType(e.target.value)}><option value="">Type: All</option><option value="client">Clients</option><option value="worker">Workers</option></select>
        <select className="field h-10 w-auto" value={status} onChange={(e) => setStatus(e.target.value)}><option value="">Status: All</option><option value="active">Active</option><option value="inactive">Inactive</option></select>
        <select className="field h-10 w-auto" value={kyc} onChange={(e) => setKyc(e.target.value)}><option value="">KYC: All</option><option value="approved">Approved</option><option value="pending">Pending</option></select>
      </div>
      {!data ? <Loading /> : data.rows.length === 0 ? <Empty text="No customers match." /> : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-mut bg-navy-50/50"><th className="p-3">Customer</th><th className="p-3">Type</th><th className="p-3">Status</th><th className="p-3">KYC</th><th className="p-3 hidden sm:table-cell">Joined</th><th className="p-3"></th></tr></thead>
            <tbody>
              {data.rows.map((u: any) => (
                <tr key={u.id} className="border-t border-line hover:bg-navy-50/40 cursor-pointer" onClick={() => { setOpenKind(u.kind === "client" ? "client" : "worker"); setOpenId(u.id); }}>
                  <td className="p-3"><div className="flex items-center gap-2.5"><div className="w-8 h-8 rounded-full bg-navy-50 text-navy grid place-items-center text-xs font-bold">{initials(u.firstName, u.lastName)}</div><div className="min-w-0"><div className="font-semibold text-navy truncate flex items-center gap-1">{u.firstName} {u.lastName}{u.isStar && <Star className="w-3.5 h-3.5 fill-brand-gold text-brand-gold" />}</div><div className="text-[11px] text-mut truncate">{u.email}</div></div></div></td>
                  <td className="p-3"><span className="chip capitalize bg-navy-50 text-navy-600">{u.kind === "both" ? "Client + Worker" : u.kind}</span></td>
                  <td className="p-3">{u.accountStatus === "active" ? <Pill kind="ok">Active</Pill> : <Pill kind="off">Inactive</Pill>}</td>
                  <td className="p-3">{u.kind === "client" ? <span className="text-mut text-xs">—</span> : (u.workerVerified ? <Pill kind="ok">Approved</Pill> : <Pill kind="pend">Pending</Pill>)}</td>
                  <td className="p-3 hidden sm:table-cell text-mut text-xs">{new Date(u.createdAt).toLocaleDateString()}</td>
                  <td className="p-3 text-right"><span className="text-brand-red font-semibold text-xs">Manage →</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-mut mt-3 text-xs">One list for all customers. Filter by type (client vs worker) or KYC status. Open a worker with pending KYC to approve or reject it.</p>
    </div>
  );
}

function ClientsSection() {
  const [q, setQ] = useState(""); const [status, setStatus] = useState("");
  const [data, setData] = useState<any>(null); const [openId, setOpenId] = useState<number | null>(null);
  async function load() { setData(await api.adminClients(q, status).catch(() => ({ stats: {}, rows: [] }))); }
  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [q, status]);
  if (openId) return <MemberDetail id={openId} kind="client" onBack={() => { setOpenId(null); load(); }} />;
  const s = data?.stats || {};
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <StripStat n={s.total ?? "—"} l="Total clients" accent="border-l-4 border-l-navy" />
        <StripStat n={s.active ?? "—"} l="Active" accent="border-l-4 border-l-brand-green" />
        <StripStat n={s.inactive ?? "—"} l="Inactive" accent="border-l-4 border-l-mut" />
        <StripStat n={(s.total ?? 0)} l="Records shown" accent="border-l-4 border-l-brand-gold" />
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        <div className="flex-1 min-w-[200px]"><SearchBox value={q} onChange={setQ} placeholder="Search by name, email…" /></div>
        <select className="field h-10 w-auto" value={status} onChange={(e) => setStatus(e.target.value)}><option value="">Status: All</option><option value="active">Active</option><option value="inactive">Inactive</option></select>
      </div>
      {!data ? <Loading /> : data.rows.length === 0 ? <Empty text="No clients match." /> : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-mut bg-navy-50/50"><th className="p-3">Client</th><th className="p-3">Status</th><th className="p-3 hidden sm:table-cell">Verified</th><th className="p-3 hidden sm:table-cell">Joined</th><th className="p-3"></th></tr></thead>
            <tbody>
              {data.rows.map((u: any) => (
                <tr key={u.id} className="border-t border-line hover:bg-navy-50/40 cursor-pointer" onClick={() => setOpenId(u.id)}>
                  <td className="p-3"><div className="flex items-center gap-2.5"><div className="w-8 h-8 rounded-full bg-navy-50 text-navy grid place-items-center text-xs font-bold">{initials(u.firstName, u.lastName)}</div><div className="min-w-0"><div className="font-semibold text-navy truncate">{u.firstName} {u.lastName}</div><div className="text-[11px] text-mut truncate">{u.email}</div></div></div></td>
                  <td className="p-3">{u.accountStatus === "active" ? <Pill kind="ok">Active</Pill> : <Pill kind="off">Inactive</Pill>}</td>
                  <td className="p-3 hidden sm:table-cell">{u.verified ? <Pill kind="ver">Verified</Pill> : <span className="text-mut text-xs">—</span>}</td>
                  <td className="p-3 hidden sm:table-cell text-mut text-xs">{new Date(u.createdAt).toLocaleDateString()}</td>
                  <td className="p-3 text-right"><span className="text-brand-red font-semibold text-xs">Manage →</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function WorkersSection() {
  const [q, setQ] = useState(""); const [status, setStatus] = useState(""); const [kyc, setKyc] = useState("");
  const [data, setData] = useState<any>(null); const [openId, setOpenId] = useState<number | null>(null);
  async function load() { setData(await api.adminWorkers(q, status, kyc).catch(() => ({ stats: {}, rows: [] }))); }
  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [q, status, kyc]);
  if (openId) return <MemberDetail id={openId} kind="worker" onBack={() => { setOpenId(null); load(); }} />;
  const s = data?.stats || {};
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <StripStat n={s.total ?? "—"} l="Total workers" accent="border-l-4 border-l-navy" />
        <StripStat n={s.active ?? "—"} l="Active" accent="border-l-4 border-l-brand-green" />
        <StripStat n={s.inactive ?? "—"} l="Inactive" accent="border-l-4 border-l-mut" />
        <StripStat n={s.pendingKyc ?? "—"} l="Pending KYC" accent="border-l-4 border-l-brand-gold" />
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        <div className="flex-1 min-w-[200px]"><SearchBox value={q} onChange={setQ} placeholder="Search by name, email…" /></div>
        <select className="field h-10 w-auto" value={status} onChange={(e) => setStatus(e.target.value)}><option value="">Status: All</option><option value="active">Active</option><option value="inactive">Inactive</option></select>
        <select className="field h-10 w-auto" value={kyc} onChange={(e) => setKyc(e.target.value)}><option value="">KYC: All</option><option value="approved">Approved</option><option value="pending">Pending</option></select>
      </div>
      {!data ? <Loading /> : data.rows.length === 0 ? <Empty text="No workers match." /> : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-mut bg-navy-50/50"><th className="p-3">Worker</th><th className="p-3">Status</th><th className="p-3">KYC</th><th className="p-3 hidden sm:table-cell">Joined</th><th className="p-3"></th></tr></thead>
            <tbody>
              {data.rows.map((u: any) => (
                <tr key={u.id} className="border-t border-line hover:bg-navy-50/40 cursor-pointer" onClick={() => setOpenId(u.id)}>
                  <td className="p-3"><div className="flex items-center gap-2.5"><div className="w-8 h-8 rounded-full bg-navy-50 text-navy grid place-items-center text-xs font-bold">{initials(u.firstName, u.lastName)}</div><div className="min-w-0"><div className="font-semibold text-navy truncate">{u.firstName} {u.lastName}</div><div className="text-[11px] text-mut truncate">{u.headline || u.email}{u.city ? ` · ${u.city}` : ""}</div></div></div></td>
                  <td className="p-3">{u.accountStatus === "active" ? <Pill kind="ok">Active</Pill> : <Pill kind="off">Inactive</Pill>}</td>
                  <td className="p-3">{u.workerVerified ? <Pill kind="ok">Approved</Pill> : <Pill kind="pend">Pending</Pill>}</td>
                  <td className="p-3 hidden sm:table-cell text-mut text-xs">{new Date(u.createdAt).toLocaleDateString()}</td>
                  <td className="p-3 text-right"><span className="text-brand-red font-semibold text-xs">Open →</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MemberDetail({ id, kind, onBack }: { id: number; kind: "client" | "worker"; onBack: () => void }) {
  const [d, setD] = useState<any>(null); const [busy, setBusy] = useState(false);
  async function load() { setD(await api.adminMemberDetail(id).catch(() => null)); }
  useEffect(() => { load(); }, [id]);
  if (!d) return <Loading />;
  const u = d.user;
  async function toggle() {
    setBusy(true);
    try { u.accountStatus === "suspended" ? await api.adminReactivate(id) : await api.adminSuspend(id); await load(); } catch { /* ignore */ } finally { setBusy(false); }
  }
  return (
    <div>
      <button onClick={onBack} className="text-sm text-mut hover:text-navy flex items-center gap-1 mb-3"><ChevronLeft className="w-4 h-4" />Back to {kind === "worker" ? "workers" : "clients"}</button>
      <div className="card p-4 flex flex-wrap items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-navy text-white grid place-items-center text-xl font-extrabold">{initials(u.firstName, u.lastName)}</div>
        <div className="flex-1 min-w-[180px]">
          <div className="text-lg font-extrabold text-navy flex items-center gap-2 flex-wrap">{u.firstName} {u.lastName}
            {u.accountStatus === "active" ? <Pill kind="ok">Active</Pill> : <Pill kind="off">Suspended</Pill>}
            {kind === "worker" && (u.workerVerified ? <Pill kind="ok">KYC Approved</Pill> : <Pill kind="pend">KYC Pending</Pill>)}
            {u.verified && <Pill kind="ver">Verified</Pill>}
          </div>
          <div className="text-sm text-mut mt-0.5">{u.email} · Member since {new Date(u.createdAt).toLocaleDateString()}</div>
        </div>
        <div className="flex gap-2">
          <Link href={`/u/${u.id}`} className="btn-outline h-9 px-3 text-xs">View public profile</Link>
          <button onClick={toggle} disabled={busy} className={`h-9 px-3 text-xs rounded-lg font-semibold disabled:opacity-50 ${u.accountStatus === "suspended" ? "btn-navy" : "btn-outline text-brand-red"}`}>{u.accountStatus === "suspended" ? <><RotateCcw className="w-3.5 h-3.5" />Reactivate</> : <><Ban className="w-3.5 h-3.5" />Suspend</>}</button>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <div className="card p-4">
          <h3 className="font-bold text-navy mb-3">Profile &amp; activity</h3>
          <div className="grid grid-cols-[130px_1fr] gap-y-2 gap-x-3 text-sm">
            <div className="text-mut">Email</div><div className="text-navy">{u.email} {u.emailVerified ? <BadgeCheck className="w-3.5 h-3.5 text-brand-green inline" /> : <span className="text-xs text-mut">(unverified)</span>}</div>
            {d.profile?.headline && <><div className="text-mut">Headline</div><div className="text-navy">{d.profile.headline}</div></>}
            {d.profile?.city && <><div className="text-mut">City</div><div className="text-navy">{d.profile.city}</div></>}
            <div className="text-mut">Activities</div><div className="text-navy capitalize">{(d.activities || []).map((a: string) => a.replace(/_/g, " ")).join(", ") || "—"}</div>
            <div className="text-mut">Wallet</div><div className="text-navy">{peso(d.wallet?.balance)} <span className="text-mut text-xs">· {peso(d.wallet?.held)} held</span></div>
            <div className="text-mut">Contracts</div><div className="text-navy">{d.counts?.contracts ?? 0}</div>
          </div>
        </div>
        <div className="card p-4">
          <h3 className="font-bold text-navy mb-3">Management</h3>
          {kind === "worker" && (
            <div className="mb-3 p-3 rounded-lg bg-brand-gold/10 border border-brand-gold/30">
              <div className="text-sm font-semibold text-navy mb-2 flex items-center gap-1.5"><IdCard className="w-4 h-4" />Worker KYC — {u.workerVerified ? "Approved" : d.kycVerificationId ? "Submitted, awaiting review" : "Not verified"}</div>
              <div className="flex gap-2 flex-wrap">
                {!u.workerVerified && <button onClick={async () => { try { if (d.kycVerificationId) await api.adminApproveWorkerVerification(d.kycVerificationId); else await api.adminSetMemberKyc(u.id, true); await load(); } catch (e: any) { alert(e?.message || "Failed."); } }} className="btn-navy h-8 px-3 text-xs"><Check className="w-3.5 h-3.5" />Approve KYC</button>}
                {!u.workerVerified && d.kycVerificationId && <button onClick={async () => { const note = prompt("Rejection reason (optional):") || undefined; try { await api.adminRejectWorkerVerification(d.kycVerificationId, note); await load(); } catch (e: any) { alert(e?.message || "Failed."); } }} className="btn-outline h-8 px-3 text-xs text-brand-red"><X className="w-3.5 h-3.5" />Reject</button>}
                {u.workerVerified && <button onClick={async () => { if (!confirm("Revoke this worker's KYC verification?")) return; try { await api.adminSetMemberKyc(u.id, false); await load(); } catch (e: any) { alert(e?.message || "Failed."); } }} className="btn-outline h-8 px-3 text-xs text-brand-red"><X className="w-3.5 h-3.5" />Revoke KYC</button>}
              </div>
              {!u.workerVerified && !d.kycVerificationId && <p className="text-[11px] text-mut mt-1.5">No documents submitted yet — approving here is a manual admin override.</p>}
            </div>
          )}
          <AdminUserEditor u={{ id: u.id, firstName: u.firstName, lastName: u.lastName, email: u.email, identityReviewPending: false }} onDone={load} />
        </div>
      </div>
    </div>
  );
}

function VerificationsSection() {
  return (
    <div className="space-y-6">
      <div><h3 className="font-bold text-navy mb-3 flex items-center gap-2"><IdCard className="w-4 h-4" />Worker KYC queue</h3><WorkerKycTab /></div>
      <div><h3 className="font-bold text-navy mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4" />Identity &amp; company verifications</h3><VerificationsTab /></div>
    </div>
  );
}

function AdminUsersSection({ selfId }: { selfId?: number }) {
  const [admins, setAdmins] = useState<any[] | null>(null);
  const [q, setQ] = useState(""); const [found, setFound] = useState<any[]>([]); const [busy, setBusy] = useState<number | null>(null);
  async function load() { setAdmins(await api.adminAdmins().catch(() => [])); }
  useEffect(() => { load(); }, []);
  useEffect(() => { if (q.trim().length < 2) { setFound([]); return; } const h = setTimeout(async () => { const r = await api.adminUsers(q).catch(() => []); setFound(r.filter((u: any) => u.userType !== "admin").slice(0, 6)); }, 300); return () => clearTimeout(h); }, [q]);
  async function setRole(id: number, make: boolean) { setBusy(id); try { await api.adminSetAdminRole(id, make); setQ(""); setFound([]); await load(); } catch (e: any) { alert(e?.message || "Failed."); } finally { setBusy(null); } }
  if (!admins) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="card p-4">
        <h3 className="font-bold text-navy mb-2">Grant admin access</h3>
        <SearchBox value={q} onChange={setQ} placeholder="Search a member by name or email…" />
        {found.length > 0 && (
          <div className="mt-2 space-y-1.5">
            {found.map((u) => (
              <div key={u.id} className="flex items-center justify-between border border-line rounded-lg p-2">
                <div className="min-w-0"><div className="font-semibold text-navy text-sm truncate">{u.name || `${u.firstName} ${u.lastName}`}</div><div className="text-[11px] text-mut truncate">{u.email}</div></div>
                <button onClick={() => setRole(u.id, true)} disabled={busy === u.id} className="btn-navy h-8 px-3 text-xs disabled:opacity-50"><Plus className="w-3.5 h-3.5" />Make admin</button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-mut bg-navy-50/50"><th className="p-3">Admin</th><th className="p-3 hidden sm:table-cell">Status</th><th className="p-3 hidden sm:table-cell">Since</th><th className="p-3"></th></tr></thead>
          <tbody>
            {admins.map((u) => (
              <tr key={u.id} className="border-t border-line">
                <td className="p-3"><div className="flex items-center gap-2.5"><div className="w-8 h-8 rounded-full bg-brand-gold/20 text-[#8a5a00] grid place-items-center text-xs font-bold">{initials(u.firstName, u.lastName)}</div><div className="min-w-0"><div className="font-semibold text-navy truncate">{u.firstName} {u.lastName} {u.id === selfId && <span className="text-[10px] text-mut">(you)</span>}</div><div className="text-[11px] text-mut truncate">{u.email}</div></div></div></td>
                <td className="p-3 hidden sm:table-cell"><Pill kind="ok">Active</Pill></td>
                <td className="p-3 hidden sm:table-cell text-mut text-xs">{new Date(u.createdAt).toLocaleDateString()}</td>
                <td className="p-3 text-right">{u.id !== selfId && <button onClick={() => setRole(u.id, false)} disabled={busy === u.id} className="btn-outline h-8 px-3 text-xs text-brand-red disabled:opacity-50">Revoke</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-mut">Granting or revoking admin access signs the member out so the change takes effect on next login.</p>
    </div>
  );
}

function downloadCsv(name: string, rows: (string | number)[][]) {
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click(); URL.revokeObjectURL(a.href);
}
function ReportsSection() {
  const [busy, setBusy] = useState<string | null>(null);
  async function revenue() {
    setBusy("rev"); try { const d = await api.adminAnalytics(); downloadCsv("revenue-by-month.csv", [["Month", "Revenue (PHP)", "GMV (PHP)"], ...(d.revenueByMonth || []).map((r: any) => [r.month, r.revenue, r.gmv])]); } finally { setBusy(null); }
  }
  async function txns() {
    setBusy("txn"); try { const d = await api.adminTransactions(); const rows: any[] = [["Type", "Title", "From", "To", "Amount (PHP)", "Status"]]; (d.orders || []).forEach((o: any) => rows.push(["order", o.itemTitle, o.buyerName, o.sellerName, o.total, o.status])); (d.contracts || []).forEach((c: any) => rows.push(["contract", c.title, c.buyerName, c.providerName, c.amount, c.status])); downloadCsv("transactions-ledger.csv", rows); } finally { setBusy(null); }
  }
  async function fees() {
    setBusy("fee"); try { const d = await api.adminFees(); downloadCsv("fees-collected.csv", [["Source", "Reference", "Gross (PHP)", "Fee %", "Fee (PHP)"], ...(d.recent || []).map((r: any) => [r.sourceType, r.sourceId, r.gross, r.feePct, r.fee])]); } finally { setBusy(null); }
  }
  const cards = [
    { tag: "Financial", title: "Revenue & fees", desc: "Platform earnings by month with GMV.", run: revenue, id: "rev" },
    { tag: "Financial", title: "Transactions ledger", desc: "All orders and contracts with amounts and status.", run: txns, id: "txn" },
    { tag: "Financial", title: "Fees collected", desc: "Per-transaction fee breakdown.", run: fees, id: "fee" },
  ];
  const soon = ["Escrow & payouts", "User growth", "KYC & verifications", "Listings & bookings"];
  return (
    <div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {cards.map((c) => (
          <div key={c.id} className="card p-4">
            <div className="text-[10px] font-extrabold uppercase tracking-wide text-brand-gold">{c.tag}</div>
            <h4 className="font-bold text-navy mt-0.5">{c.title}</h4>
            <p className="text-xs text-mut mt-1 mb-3">{c.desc}</p>
            <div className="flex gap-2"><button onClick={() => window.print()} className="btn-outline h-8 px-3 text-xs"><Printer className="w-3.5 h-3.5" />Print</button><button onClick={c.run} disabled={busy === c.id} className="btn-navy h-8 px-3 text-xs disabled:opacity-50">{busy === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}Export CSV</button></div>
          </div>
        ))}
        {soon.map((t) => (
          <div key={t} className="card p-4 opacity-70"><div className="text-[10px] font-extrabold uppercase tracking-wide text-mut">Coming next</div><h4 className="font-bold text-navy mt-0.5">{t}</h4><p className="text-xs text-mut mt-1">Available in the next reporting module update.</p></div>
        ))}
      </div>
    </div>
  );
}

function EmailOutboxSection() {
  const [d, setD] = useState<any>(null); const [busy, setBusy] = useState(false);
  async function load() { setD(await api.adminEmailOutbox().catch(() => null)); }
  useEffect(() => { load(); }, []);
  async function retry() { setBusy(true); try { await api.adminRetryEmails(); await load(); } catch (e: any) { alert(e?.message || "Failed."); } finally { setBusy(false); } }
  if (!d) return <Loading />;
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <StripStat n={d.total || 0} l="Total" accent="border-l-4 border-l-navy" />
        <StripStat n={d.sent || 0} l="Sent" accent="border-l-4 border-l-brand-green" />
        <StripStat n={d.failed || 0} l="Failed" accent="border-l-4 border-l-brand-red" />
        <StripStat n={d.queued || 0} l="Queued" accent="border-l-4 border-l-brand-gold" />
      </div>
      <div className="flex justify-end mb-3"><button onClick={retry} disabled={busy} className="btn-navy h-9 px-3 text-xs disabled:opacity-50">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}Retry failed</button></div>
      {(!d.recent || d.recent.length === 0) ? <Empty text="No emails recorded yet." /> : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-mut bg-navy-50/50"><th className="p-3">To</th><th className="p-3">Subject</th><th className="p-3">Status</th><th className="p-3 hidden lg:table-cell">Error</th><th className="p-3 hidden sm:table-cell">When</th></tr></thead>
            <tbody>
              {d.recent.map((r: any) => (
                <tr key={r.id} className="border-t border-line align-top">
                  <td className="p-3 text-navy">{r.to}</td>
                  <td className="p-3 text-navy-700">{r.subject}</td>
                  <td className="p-3">{r.status === "sent" ? <Pill kind="ok">Sent</Pill> : r.status === "failed" ? <span className="chip bg-brand-red/10 text-brand-red">Failed</span> : <Pill kind="pend">{r.status}</Pill>}</td>
                  <td className="p-3 hidden lg:table-cell text-[11px] text-brand-red max-w-[280px] break-words">{r.lastError || "—"}</td>
                  <td className="p-3 hidden sm:table-cell text-mut text-xs whitespace-nowrap">{new Date(r.createdAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MediaModerationSection() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState<any[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  async function load() { setRows(null); setRows(await api.adminMedia(status).catch(() => [])); }
  useEffect(() => { load(); }, [status]);
  async function decide(id: string, decision: "approve" | "reject") {
    setBusy(id + decision);
    try { const note = decision === "reject" ? (prompt("Reason (optional, shown to the user):") || undefined) : undefined; await api.adminModerateMedia(id, decision, note); await load(); }
    catch (e: any) { alert(e?.message || "Failed."); } finally { setBusy(null); }
  }
  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`h-8 px-3 rounded-lg text-xs font-semibold capitalize ${status === s ? "bg-brand-red text-white" : "bg-navy-50 text-mut hover:text-navy"}`}>{s}</button>
        ))}
      </div>
      {!rows ? <Loading /> : rows.length === 0 ? <Empty text={`No ${status === "all" ? "" : status + " "}media.`} /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((m) => (
            <div key={m.id} className="card p-3">
              <div className="aspect-video rounded-lg overflow-hidden bg-navy-50 mb-2 grid place-items-center">
                <img src={m.url} alt={m.title || "media"} className="w-full h-full object-cover" onError={(e) => ((e.target as HTMLImageElement).style.display = "none")} />
              </div>
              <div className="text-sm font-semibold text-navy truncate">{m.title || "Untitled"}</div>
              <div className="text-[11px] text-mut truncate">by {m.ownerName || `user ${m.userId}`} · {new Date(m.createdAt).toLocaleDateString()}</div>
              <div className="mt-1"><Pill kind={m.status === "approved" ? "ok" : m.status === "rejected" ? "off" : "pend"}>{m.status}</Pill></div>
              {m.status !== "approved" && (
                <div className="flex gap-2 mt-2">
                  <button onClick={() => decide(m.id, "approve")} disabled={busy === m.id + "approve"} className="btn-navy h-8 px-3 text-xs disabled:opacity-50"><Check className="w-3.5 h-3.5" />Approve</button>
                  <button onClick={() => decide(m.id, "reject")} disabled={busy === m.id + "reject"} className="btn-outline h-8 px-3 text-xs text-brand-red disabled:opacity-50"><X className="w-3.5 h-3.5" />Reject</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SiteSection() {
  const [rows, setRows] = useState<any[] | null>(null);
  const [editing, setEditing] = useState<any | null>(null);
  async function load() { setRows(await api.adminBanners().catch(() => [])); }
  useEffect(() => { load(); }, []);
  const blank = { title: "", subtitle: "", ctaLabel: "", ctaHref: "", imageUrl: "", slot: "promo", orderIndex: 0, active: true };
  async function save(b: any) {
    try { if (b.id) await api.adminUpdateBanner(b.id, b); else await api.adminCreateBanner(b); setEditing(null); await load(); }
    catch (e: any) { alert(e?.message || "Could not save."); }
  }
  async function remove(id: string) { if (!confirm("Delete this banner?")) return; try { await api.adminDeleteBanner(id); await load(); } catch (e: any) { alert(e?.message || "Failed."); } }
  if (!rows) return <Loading />;
  return (
    <div>
      <div className="flex items-center justify-between mb-4"><h3 className="font-bold text-navy">Homepage banners</h3><button onClick={() => setEditing(blank)} className="btn-navy h-9 px-3 text-xs"><Plus className="w-4 h-4" />New banner</button></div>
      {editing && (
        <div className="card p-4 mb-4 space-y-2">
          <div className="grid sm:grid-cols-2 gap-2">
            <div><label className="label">Title</label><input className="field" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
            <div><label className="label">Slot</label><select className="field" value={editing.slot} onChange={(e) => setEditing({ ...editing, slot: e.target.value })}><option value="hero">Hero</option><option value="promo">Promo</option><option value="announcement">Announcement</option></select></div>
          </div>
          <div><label className="label">Subtitle</label><input className="field" value={editing.subtitle || ""} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} /></div>
          <div className="grid sm:grid-cols-2 gap-2">
            <div><label className="label">Button label</label><input className="field" value={editing.ctaLabel || ""} onChange={(e) => setEditing({ ...editing, ctaLabel: e.target.value })} /></div>
            <div><label className="label">Button link</label><input className="field" value={editing.ctaHref || ""} onChange={(e) => setEditing({ ...editing, ctaHref: e.target.value })} placeholder="/marketplace" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            <div><label className="label">Image URL (optional)</label><input className="field" value={editing.imageUrl || ""} onChange={(e) => setEditing({ ...editing, imageUrl: e.target.value })} /></div>
            <div><label className="label">Order</label><input type="number" className="field" value={editing.orderIndex ?? 0} onChange={(e) => setEditing({ ...editing, orderIndex: Number(e.target.value) })} /></div>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />Active (visible on the homepage)</label>
          <div className="flex gap-2"><button onClick={() => save(editing)} className="btn-red h-9"><Check className="w-4 h-4" />Save</button><button onClick={() => setEditing(null)} className="btn-outline h-9">Cancel</button></div>
        </div>
      )}
      {rows.length === 0 ? <Empty text="No banners yet. Create one to feature it on the homepage." /> : (
        <div className="space-y-2">
          {rows.map((b) => (
            <div key={b.id} className="card p-3 flex items-center gap-3">
              <div className="w-24 h-12 rounded-lg shrink-0 bg-gradient-to-br from-navy to-brand-red" style={b.imageUrl ? { backgroundImage: `url(${b.imageUrl})`, backgroundSize: "cover" } : undefined} />
              <div className="flex-1 min-w-0"><div className="font-semibold text-navy truncate">{b.title}</div><div className="text-[11px] text-mut">{b.slot} · order {b.orderIndex} · {b.active ? <span className="text-brand-green">Live</span> : <span className="text-mut">Hidden</span>}</div></div>
              <div className="flex gap-2 shrink-0"><button onClick={() => setEditing(b)} className="btn-outline h-8 px-3 text-xs">Edit</button><button onClick={() => remove(b.id)} className="btn-outline h-8 px-3 text-xs text-brand-red"><Trash2 className="w-3.5 h-3.5" /></button></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
